import { useLocale } from "@/contexts/LocaleContext";
import { Linkedin } from "lucide-react";

const MEMBERS = [
  {
    key: "member1",
    initials: "GB",
    photo: "https://customer-assets.emergentagent.com/job_agro-trade-17/artifacts/lc41o8ge_image%20%281%29.png",
    linkedin: "https://www.linkedin.com/in/acurcio-barbosa/",
  },
  {
    key: "member2",
    initials: "AP",
    photo: "https://customer-assets.emergentagent.com/job_agro-trade-17/artifacts/fzgcqodm_image.png",
    linkedin: "https://www.linkedin.com/in/nbalves/",
  },
  {
    key: "member3",
    initials: "CS",
    photo: "https://customer-assets.emergentagent.com/job_agro-trade-17/artifacts/vvll8isl_image%20%283%29.png",
    linkedin: "https://www.linkedin.com/in/celiosales/",
  },
  {
    key: "member4",
    initials: "VM",
    photo: "https://customer-assets.emergentagent.com/job_agro-trade-17/artifacts/pudal5d7_image%20%282%29.png",
    linkedin: null,
  },
];

export const TeamSection = () => {
  const { t } = useLocale();

  return (
    <section className="relative border-y border-border overflow-hidden">
      {/* Background image with overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1599639957043-f3aa5c986398?auto=format&fit=crop&w=1920&h=1080"
          alt=""
          className="h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background" />
        <div className="absolute inset-0 bg-[#5e1f0c]/10 dark:bg-[#0c0604]/40" />
      </div>

      <div className="relative z-10 mx-auto max-w-[1600px] px-4 sm:px-8 py-20 lg:py-28">
        <div className="max-w-3xl">
          <div className="font-mono-data text-[10px] uppercase tracking-[0.3em] text-accent mb-4 flex items-center gap-3">
            <span className="h-px w-12 bg-accent" />
            {t("team.tagline")}
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-black leading-[1.05] text-balance">
            {t("team.title")}
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground mt-5 max-w-2xl leading-relaxed text-pretty">
            {t("team.subtitle")}
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {MEMBERS.map((m) => (
            <article
              key={m.key}
              data-testid={`team-card-${m.key}`}
              className="group bg-card/95 backdrop-blur-sm border border-border hover-lift hover:border-accent/50"
            >
              {/* Photo placeholder */}
              <div className="aspect-[4/5] relative overflow-hidden bg-secondary">
                {m.photo ? (
                  <img src={m.photo} alt={t(`team.${m.key}_name`)} className="h-full w-full object-cover" />
                ) : (
                  <PhotoPlaceholder initials={m.initials} t={t} />
                )}
                {/* Subtle border accent on hover */}
                <div className="absolute inset-x-0 bottom-0 h-1 bg-accent scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />
              </div>

              <div className="p-6">
                <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-accent mb-2">
                  {t(`team.${m.key}_role`)}
                </div>
                <h3 className="font-display text-xl tracking-tight font-black leading-tight">
                  {t(`team.${m.key}_name`)}
                </h3>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                  {t(`team.${m.key}_bio`)}
                </p>
                <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                  <span className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                    {t("team.leadership")}
                  </span>
                  {m.linkedin ? (
                    <a
                      href={m.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`LinkedIn — ${t(`team.${m.key}_name`)}`}
                      data-testid={`team-${m.key}-linkedin`}
                      className="text-muted-foreground/60 hover:text-accent transition-colors"
                    >
                      <Linkedin className="h-3.5 w-3.5" />
                    </a>
                  ) : (
                    <Linkedin className="h-3.5 w-3.5 text-muted-foreground/20" aria-hidden="true" />
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

const PhotoPlaceholder = ({ initials, t }) => (
  <div className="relative h-full w-full flex items-center justify-center bg-gradient-to-br from-[#5e1f0c] via-[#9c6b51] to-[#3a1306] overflow-hidden">
    {/* Decorative grain */}
    <div
      className="absolute inset-0 opacity-30 mix-blend-overlay"
      style={{
        backgroundImage:
          "radial-gradient(circle at 30% 30%, rgba(233,184,122,0.4) 0%, transparent 50%), radial-gradient(circle at 70% 70%, rgba(252,251,250,0.15) 0%, transparent 50%)",
      }}
    />
    {/* Initials */}
    <div className="relative font-display text-7xl font-black text-[#fcfbfa] tracking-tighter">
      {initials}
    </div>
    {/* Bottom badge */}
    <div className="absolute bottom-4 left-4 font-mono-data text-[9px] uppercase tracking-[0.25em] text-[#fcfbfa]/60">
      {t ? t("team.photo_soon") : "Photo · soon"}
    </div>
  </div>
);
